import { supportedLanguages } from "@/i18n/languages";
import { changeLanguage, getCachedLanguage } from "@/lib/utils";
import {
  FormControl,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import React from "react";
import { useTranslation } from "react-i18next";

const FlagImage = styled("img")({
  borderRadius: "50%",
  width: 22,
  height: 22,
  objectFit: "cover",
  border: "1px solid rgba(0,0,0,0.1)",
  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
});

const LanguageSelect: React.FC = () => {
  const { i18n } = useTranslation();

  const selectedLanguageCode = getCachedLanguage(i18n);

  const handleLanguageSelect = (languageCode: string) => {
    changeLanguage(i18n, languageCode);
  };

  const currentLanguage = supportedLanguages.find(
    language => language.code === selectedLanguageCode
  );

  return (
    <FormControl size="small">
      <Select
        id="language-select"
        autoWidth
        value={selectedLanguageCode}
        onChange={event => handleLanguageSelect(event.target.value as string)}
        renderValue={() => (
          <Stack direction="row" spacing={1} alignItems="center">
            {currentLanguage && (
              <FlagImage
                src={currentLanguage.icon}
                alt={currentLanguage.common}
              />
            )}
            <Typography
              variant="body2"
              sx={{ fontWeight: 500, color: "text.primary" }}
            >
              {currentLanguage?.common}
            </Typography>
          </Stack>
        )}
        sx={{
          minWidth: 80,
          "& .MuiOutlinedInput-notchedOutline": {
            border: "none",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            border: "none",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            border: "none",
          },
          "& .MuiSelect-select": {
            py: 0.75,
            px: 1,
          },
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              mt: 0.5,
              borderRadius: 2,
              boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
            },
          },
        }}
      >
        {supportedLanguages.map(language => (
          <MenuItem
            key={language.code}
            value={language.code}
            sx={{
              py: 1,
              px: 2,
              borderRadius: 1,
              mx: 0.5,
              "&:hover": {
                backgroundColor: "action.hover",
              },
              "&.Mui-selected": {
                backgroundColor: "primary.light",
                "&:hover": {
                  backgroundColor: "primary.light",
                },
              },
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <FlagImage src={language.icon} alt={language.common} />
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {language.common}
              </Typography>
            </Stack>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default LanguageSelect;
